import { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Home, ChevronDown, Star } from 'lucide-react';
import ProductGrid from '../components/ProductGrid';
import SearchDropdown from '../components/SearchDropdown';
import {
  fetchProductsByCategory,
  fetchProductsWithCount,
  fetchCategoryById,
  fetchAllParentCategories,
  fetchCategoryTree,
  fetchAllCategoriesFlat,
} from '../utils/apiHelpers';
import type { Product, Category } from '../utils/apiHelpers';

const findCategoryInTree = (items: Category[], target: string): Category | null => {
  const normalizedTarget = target.trim().toLowerCase();
  if (!normalizedTarget) return null;

  const visit = (list: Category[]): Category | null => {
    for (const item of list) {
      const slug = (item.slug ?? '').toLowerCase();
      const name = (item.name ?? '').toLowerCase();
      if (slug === normalizedTarget || name === normalizedTarget || slug.includes(normalizedTarget) || name.includes(normalizedTarget)) {
        return item;
      }

      const childMatch = visit(item.children ?? []);
      if (childMatch) return childMatch;
    }
    return null;
  };

  return visit(items);
};

export default function ShopPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const categoryParam = searchParams.get('category');
  const includeSubParam = searchParams.get('include_sub');
  const queryParam = searchParams.get('q') ?? '';
  
  const includeSubcategories = includeSubParam !== 'false';

  // Check if category is numeric ID or string slug
  const categoryId =
    categoryParam && !Number.isNaN(Number(categoryParam))
      ? Number(categoryParam)
      : null;
  
  const categorySlug = categoryParam && Number.isNaN(Number(categoryParam))
    ? categoryParam.toLowerCase()
    : null;

  const [products, setProducts] = useState<Product[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [categoryName, setCategoryName] = useState('');
  const [parentCategories, setParentCategories] = useState<Category[]>([]);
  const [categoryTree, setCategoryTree] = useState<Category[]>([]);
  const [categoriesOpen, setCategoriesOpen] = useState(true);
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  const safeCategoryTree = Array.isArray(categoryTree) ? categoryTree : [];

  const [searchQuery, setSearchQuery] = useState(() => queryParam);
  const [priceMin, setPriceMin] = useState<number | null>(null);
  const [priceMax, setPriceMax] = useState<number | null>(null);
  const [priceBounds, setPriceBounds] = useState<{ min: number; max: number }>({ min: 0, max: 1000 });
  const [brandFilter, setBrandFilter] = useState('');
  const [selectedBrands, setSelectedBrands] = useState<string[]>([]);
  const [brandNames, setBrandNames] = useState<string[]>([]);
  const [inStockOnly, setInStockOnly] = useState(false);
  const [sortBy, setSortBy] = useState('default');
  const [minRating, setMinRating] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchAllParentCategories()
      .then((list) => {
        if (!cancelled) setParentCategories(list);
      })
      .catch(() => {
        if (!cancelled) setParentCategories([]);
      });
    fetchCategoryTree()
      .then((tree) => {
        if (cancelled) return;
        setCategoryTree(Array.isArray(tree) ? tree : []);
      })
      .catch((error) => {
        console.error('Failed to load category tree', error);
        if (!cancelled) setCategoryTree([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadProductsForCategory = async () => {
      if (!categoryId && !categorySlug) {
        setCategoryName('');
        setLoading(true);
        try {
          const { results, count } = await fetchProductsWithCount({}, 48);
          if (cancelled) return;
          setProducts(results);
          setTotalCount(count);
          const names = Array.from(new Set(results.map((item) => (item.vendor_name || '').trim()).filter(Boolean))).sort();
          setBrandNames(names);
        } catch {
          if (!cancelled) {
            setProducts([]);
            setTotalCount(0);
          }
        } finally {
          if (!cancelled) setLoading(false);
        }
        return;
      }

      setLoading(true);
      setCategoryName('');

      try {
        // Resolve category id. If slug is provided, try the loaded tree first,
        // then fall back to a flat categories scan. If unresolved, show no products.
        let resolvedCategoryId = categoryId ?? null;
        if (!resolvedCategoryId && categorySlug) {
          const foundInTree = findCategoryInTree(categoryTree, categorySlug);
          if (foundInTree) resolvedCategoryId = foundInTree.id;
          else {
            try {
              const flat = await fetchAllCategoriesFlat();
              if (cancelled) return;
              const normalized = categorySlug.trim().toLowerCase();
              const matched = flat.find((c: Category) =>
                ((c.slug ?? String(c.name ?? '')).toLowerCase() === normalized) ||
                ((c.name ?? '').toLowerCase() === normalized)
              );
              if (matched) resolvedCategoryId = matched.id;
            } catch {
              // ignore
            }
          }
        }

        if (!resolvedCategoryId) {
          // If we couldn't resolve the category param to an ID, return empty result
          // to avoid fuzzy matches — this ensures strict, predictable behavior.
          setCategoryName(categorySlug ?? '');
          setProducts([]);
          setTotalCount(0);
          return;
        }

        const category = await fetchCategoryById(resolvedCategoryId);

        const categoryProducts = await fetchProductsByCategory(resolvedCategoryId, includeSubcategories, 48);

        if (cancelled) return;

        setCategoryName(category?.name ?? '');
        setProducts(categoryProducts);
        setTotalCount(categoryProducts.length);
      } catch {
        if (!cancelled) {
          setProducts([]);
          setTotalCount(0);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadProductsForCategory();
    return () => {
      cancelled = true;
    };
  }, [categoryId, categorySlug, categoryTree]);

  useEffect(() => {
    const names = Array.from(
      new Set(
        products
          .map((item) => (item.vendor_name || '').trim())
          .filter((name) => name)
      )
    ).sort();
    setBrandNames(names);
  }, [products]);

  // Compute price bounds from loaded products and update slider values
  useEffect(() => {
    const prices = products
      .map((p) => parseFloat(p.price))
      .filter((v) => !Number.isNaN(v) && isFinite(v));
    const min = prices.length ? Math.floor(Math.min(...prices)) : 0;
    const max = prices.length ? Math.ceil(Math.max(...prices)) : 1000;
    setPriceBounds({ min, max });

    setPriceMin((current) => {
      if (current == null) return min;
      if (current < min) return min;
      if (current > max) return max;
      return current;
    });
    setPriceMax((current) => {
      if (current == null) return max;
      if (current > max) return max;
      if (current < min) return min;
      return current;
    });
  }, [products]);

  const filteredProducts = useMemo(() => {
    let list = [...products];
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter((p) => p.name.toLowerCase().includes(q));
    }
    if (priceMin != null && !Number.isNaN(priceMin)) {
      const min = priceMin;
      list = list.filter((p) => parseFloat(p.price) >= min);
    }
    if (priceMax != null && !Number.isNaN(priceMax)) {
      const max = priceMax;
      list = list.filter((p) => parseFloat(p.price) <= max);
    }
    if (selectedBrands.length > 0) {
      list = list.filter((p) => selectedBrands.includes((p.vendor_name || '').trim()));
    }
    if (minRating != null) {
      list = list.filter((p) => {
        const r = p.rating != null ? Number(p.rating) : 0;
        return !Number.isNaN(r) && r >= minRating;
      });
    }
    if (inStockOnly) {
      list = list.filter((p) => p.stock != null && p.stock > 0);
    }
    if (sortBy === 'price_asc') {
      list.sort((a, b) => parseFloat(a.price) - parseFloat(b.price));
    } else if (sortBy === 'price_desc') {
      list.sort((a, b) => parseFloat(b.price) - parseFloat(a.price));
    } else if (sortBy === 'name') {
      list.sort((a, b) => a.name.localeCompare(b.name));
    }
    return list;
  }, [products, searchQuery, priceMin, priceMax, selectedBrands, inStockOnly, sortBy]);

  // compute slider fill positions
  const rangeMin = priceBounds.min;
  const rangeMax = priceBounds.max;
  const minPerc = rangeMax > rangeMin ? (((priceMin ?? rangeMin) - rangeMin) / (rangeMax - rangeMin)) * 100 : 0;
  const maxPerc = rangeMax > rangeMin ? (((priceMax ?? rangeMax) - rangeMin) / (rangeMax - rangeMin)) * 100 : 100;

  const displayedCount = filteredProducts.length;
  const totalForLabel = categoryId ? displayedCount : totalCount;

  const handleResetFilters = () => {
    setSearchQuery('');
    setPriceMin(null);
    setPriceMax(null);
    setBrandFilter('');
    setSelectedBrands([]);
    setInStockOnly(false);
    setSortBy('default');
    setSearchParams({});
  };

  useEffect(() => {
    setSearchQuery(queryParam);
  }, [queryParam]);

  const handleCategorySelect = (id: number | null) => {
    if (id === null) setSearchParams({});
    else setSearchParams({ category: String(id), include_sub: 'true' });
  };

  return (
    <main className="bg-[var(--background)] text-[var(--foreground)]">
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Breadcrumb */}
        <nav className="text-sm text-gray-500 mb-6">
          <Link to="/" className="hover:underline">Home</Link>
          <span className="mx-1">/</span>
          <Link to="/shop" className="hover:underline">Shop</Link>
          {categoryName && (
            <>
              <span className="mx-1">/</span>
              <span className="text-gray-700">{categoryName}</span>
            </>
          )}
        </nav>

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Left Sidebar - Filters */}
          <aside className="w-full lg:w-64 flex-shrink-0">
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 sticky top-24">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-semibold text-[var(--foreground)]">Filters</h2>
                <button
                  type="button"
                  onClick={handleResetFilters}
                  className="text-sm text-[#ff9100] hover:underline"
                >
                  Reset
                </button>
              </div>

              <div className="space-y-4">
                <div className="relative">
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Search products
                  </label>
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      if (e.target.value.trim()) {
                        setIsSearchOpen(true);
                      } else {
                        setIsSearchOpen(false);
                      }
                    }}
                    onFocus={() => searchQuery.trim() && setIsSearchOpen(true)}
                    onBlur={() => setTimeout(() => setIsSearchOpen(false), 200)}
                    placeholder="Search products"
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                  />
                  {isSearchOpen && searchQuery.trim() && (
                    <div className="absolute inset-x-0 top-full z-20 mt-1 rounded-lg border border-gray-200 bg-white shadow-lg">
                      <SearchDropdown query={searchQuery} onClose={() => setIsSearchOpen(false)} />
                    </div>
                  )}
                </div>

                {/* Categories - parent only */}
                <div>
                  <button
                    type="button"
                    onClick={() => setCategoriesOpen(!categoriesOpen)}
                    className="flex items-center justify-between w-full text-left"
                  >
                    <span className="text-xs font-medium text-gray-600">
                      Categories
                    </span>
                    <span className="text-gray-500 text-xs">
                      {parentCategories.length} total
                    </span>
                    <ChevronDown
                      className={`h-4 w-4 transition-transform ${categoriesOpen ? 'rotate-180' : ''}`}
                    />
                  </button>
                  <div className="mt-2">
                    <button
                      type="button"
                      onClick={() => handleCategorySelect(null)}
                      className={`w-full flex items-center gap-2 px-3 py-2 rounded text-sm font-medium mb-2 ${
                        !categoryId
                          ? 'bg-[#ff9100] text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      All Categories
                      <ChevronDown className="h-4 w-4 ml-auto opacity-70" />
                    </button>
                    {categoriesOpen && (
                      <div className="max-h-48 overflow-y-auto space-y-0.5 pr-1">
                        {parentCategories.map((cat) => (
                          <button
                            key={cat.id}
                            type="button"
                            onClick={() => handleCategorySelect(cat.id)}
                            className={`w-full text-left px-3 py-1.5 rounded text-sm flex items-center justify-between ${
                              categoryId === cat.id
                                ? 'bg-orange-50 text-[#ff9100] font-medium'
                                : 'text-gray-700 hover:bg-gray-50'
                            }`}
                          >
                            {cat.name}
                            <span className="text-xs text-gray-400">
                              {safeCategoryTree.find((treeCat) => treeCat.id === cat.id)?.children?.length ?? 0} subcats
                            </span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {categoryTree.length > 0 && (
                    <div className="mt-4 text-sm text-gray-700">
                      <div className="font-semibold text-xs uppercase tracking-wider text-gray-500 mb-2">Category tree</div>
                      <div className="space-y-3 max-h-64 overflow-y-auto border border-gray-200 rounded p-3 bg-gray-50 text-xs text-gray-700">
                        {safeCategoryTree.map((root) => (
                          <div key={root.id} className="space-y-2">
                            <div className="font-medium text-gray-800">{root.name}</div>
                            {root.children && root.children.length > 0 && (
                              <div className="ml-3 space-y-1">
                                {root.children.map((child) => (
                                  <div key={child.id}>
                                    <div className="flex items-center justify-between gap-2">
                                      <button
                                        type="button"
                                        onClick={() => handleCategorySelect(child.id)}
                                        className="text-left text-gray-700 hover:text-[#ff9100]"
                                      >
                                        • {child.name}
                                      </button>
                                      <span className="text-[11px] text-gray-400">
                                        {child.children?.length ?? 0}
                                      </span>
                                    </div>
                                    {child.children && child.children.length > 0 && (
                                      <div className="ml-4 mt-1 space-y-1 text-gray-600">
                                        {child.children.map((grand) => (
                                          <button
                                            key={grand.id}
                                            type="button"
                                            onClick={() => handleCategorySelect(grand.id)}
                                            className="text-left text-gray-600 hover:text-[#ff9100]"
                                          >
                                            - {grand.name}
                                          </button>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Price
                  </label>
                  <div className="text-sm text-gray-600 mb-2">
                    {priceMin != null && priceMax != null
                      ? `$${priceMin} — $${priceMax}`
                      : 'Any'}
                  </div>

                  <div>
                    <style>{` 
                      .range-input { background: transparent; }
                      .range-input::-webkit-slider-thumb { -webkit-appearance:none; appearance:none; height:16px; width:16px; border-radius:9999px; background:#ff9100; border:2px solid #fff; box-shadow:0 2px 6px rgba(0,0,0,0.18); cursor:pointer; }
                      .range-input::-moz-range-thumb { height:16px; width:16px; border-radius:9999px; background:#ff9100; border:2px solid #fff; box-shadow:0 2px 6px rgba(0,0,0,0.18); cursor:pointer; }
                      .range-input::-webkit-slider-runnable-track { height:6px; background: transparent; }
                    `}</style>

                    <div className="relative h-8 mb-2">
                      <input
                        type="range"
                        min={priceBounds.min}
                        max={priceBounds.max}
                        value={priceMin ?? priceBounds.min}
                        onChange={(e) => {
                          const v = Number(e.target.value);
                          // allow start to push end: if start passes current max, move max up to start
                          if (priceMax != null && v > priceMax) {
                            setPriceMax(v);
                          }
                          setPriceMin(v);
                        }}
                        className="range-input absolute left-0 top-2 w-full appearance-none h-2 bg-transparent z-30"
                      />
                      <input
                        type="range"
                        min={priceBounds.min}
                        max={priceBounds.max}
                        value={priceMax ?? priceBounds.max}
                        onChange={(e) => {
                          const v = Number(e.target.value);
                          // allow end to push start: if end goes below current min, move min down to end
                          if (priceMin != null && v < priceMin) {
                            setPriceMin(v);
                          }
                          setPriceMax(v);
                        }}
                        className="range-input absolute left-0 top-2 w-full appearance-none h-2 bg-transparent z-20"
                      />

                      <div className="pointer-events-none absolute left-0 right-0 top-4 h-1 bg-gray-200 rounded" />
                      <div
                        className="pointer-events-none absolute top-4 h-1 rounded"
                        style={{
                          left: `${Math.max(0, minPerc)}%`,
                          width: `${Math.max(0, maxPerc - minPerc)}%`,
                          background: 'linear-gradient(90deg,#ff9100,#ffb84d)'
                        }}
                      />
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <input
                      type="number"
                      value={priceMin ?? ''}
                      onChange={(e) => setPriceMin(e.target.value === '' ? null : Number(e.target.value))}
                      placeholder="Min"
                      className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
                    />
                    <input
                      type="number"
                      value={priceMax ?? ''}
                      onChange={(e) => setPriceMax(e.target.value === '' ? null : Number(e.target.value))}
                      placeholder="Max"
                      className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Find a brand
                  </label>
                  <input
                    type="text"
                    value={brandFilter}
                    onChange={(e) => setBrandFilter(e.target.value)}
                    placeholder="Find a brand"
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                  />
                  <div className="mt-2 space-y-1 max-h-40 overflow-y-auto border border-gray-200 rounded p-2 bg-gray-50 text-sm">
                    {(brandNames
                      .filter((brand) =>
                        brandFilter.trim()
                          ? brand.toLowerCase().includes(brandFilter.toLowerCase())
                          : true
                      )
                      .slice(0, 20)
                      .map((brand) => (
                        <label key={brand} className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={selectedBrands.includes(brand)}
                            onChange={() => {
                              setSelectedBrands((current) =>
                                current.includes(brand)
                                  ? current.filter((b) => b !== brand)
                                  : [...current, brand]
                              );
                            }}
                            className="h-4 w-4 rounded border-gray-300 text-[#ff9100]"
                          />
                          <span>{brand}</span>
                        </label>
                      ))) || <div className="text-xs text-gray-400">No brands found.</div>}
                  </div>
                    <div className="mt-3">
                      <label className="block text-xs font-medium text-gray-600 mb-2">Rating</label>
                      <div className="grid gap-2">
                        {[null, 4, 3, 2, 1].map((r) => (
                          <button
                            key={String(r)}
                            type="button"
                            onClick={() => setMinRating(r as number | null)}
                            className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition ${
                              minRating === r
                                ? 'border-[#ff9100] bg-[#fff6ef] text-[#a04900]'
                                : 'border-gray-200 bg-white text-gray-700 hover:border-[#ff9100] hover:bg-[#fff7ec]'
                            }`}
                          >
                            {r == null ? (
                              <span className="text-sm">Any</span>
                            ) : (
                              <>
                                <span className="flex items-center gap-1">
                                  {Array.from({ length: r }).map((_, i) => (
                                    <Star key={i} className="h-4 w-4 text-[#ff9100]" />
                                  ))}
                                </span>
                                <span className="text-[10px] text-gray-500">+ Rating</span>
                              </>
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                  {selectedBrands.length > 0 && (
                    <div className="mt-2 text-xs text-gray-500">
                      Selected brands: {selectedBrands.join(', ')}
                    </div>
                  )}
                </div>

                <div className="pt-3 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => navigate('/checkout')}
                    className="w-full rounded bg-[#ff9100] px-3 py-2 text-sm font-semibold text-white hover:bg-[#e07f00]"
                  >
                    Checkout
                  </button>
                </div>

                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={inStockOnly}
                    onChange={(e) => setInStockOnly(e.target.checked)}
                    className="rounded border-gray-300 text-[#ff9100]"
                  />
                  <span className="text-sm text-gray-700">In stock only</span>
                </label>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Variation
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. Small, Blue"
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                  />
                </div>
              </div>
            </div>
          </aside>

          {/* Right - Banner, toolbar, grid */}
          <div className="flex-1 min-w-0">
            {/* Banner */}
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 mb-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#ff9100]/20 flex items-center justify-center">
                <Home className="h-5 w-5 text-[#ff9100]" />
              </div>
              <div>
                <h1 className="font-semibold text-[var(--foreground)]">
                  {categoryName || 'All Categories'}
                </h1>
                <p className="text-sm text-gray-500">
                  Showing {displayedCount} products
                  {parentCategories.length > 0 &&
                    ` from ${parentCategories.length} categories`}
                </p>
              </div>
            </div>

            {/* Toolbar */}
            <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
              <p className="text-sm text-gray-600">
                Showing {filteredProducts.length} of {categoryId ? displayedCount : totalForLabel} results
              </p>
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-600">Sort:</label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="border border-gray-300 rounded px-3 py-1.5 text-sm bg-white"
                >
                  <option value="default">Default sorting</option>
                  <option value="price_asc">Price: low to high</option>
                  <option value="price_desc">Price: high to low</option>
                  <option value="name">Name</option>
                </select>
              </div>
            </div>

            {/* Product grid */}
            <ProductGrid
              products={filteredProducts}
              loading={loading}
            />
          </div>
        </div>
      </div>
    </main>
  );
}
